import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { TableContent } from "components/__new/common/TableContent";
import { DateTimeFormat } from "utils/dateFormat";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import processParams from "utils/processParams";
import Filter, { ActiveFilter } from "components/__new/common/Filter";
import * as auditTrailAction from "store/entities/auditTrail/action";
import ContentLoading from "components/__new/common/ContentLoading";
import pluralize from "utils/pluralize";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import { getRandomColor } from "utils/randomColor";
import useLoginInfo from "hooks/useLoginInfo";

export const List = () => {
    const location = useLocation();
    const { userInfo } = useLoginInfo();
    const dispatch = useDispatch();
    const {urlQueryParams} = useUrlQueryParams();
    const { auditTrailLoading, auditTrail, auditTrailDownloadLoading, auditTrailDownload } = useSelector((s) => s.entities.auditTrail);

    const downloadAsCSVRef = useRef();
    // const printComponentRef = useRef();
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });

    const getAuditTrail = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "keyword");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");
        params = processParams(urlQueryParams, params, "dateRange", "string", "", "dateRange");
        params = processParams(urlQueryParams, params, "module", "string", "", "module");
        params = processParams(urlQueryParams, params, "module-action", "string", "", "action");

        setListParams(params);
        dispatch(auditTrailAction.getAuditTrail({...params, employerId: userInfo?.employer?._id}));
    }


    useEffect(() => {
        getAuditTrail();
        // eslint-disable-next-line
    }, [location]);


    return (
        <div>

            <DynamicHeadTag
                headerText="Audit Logs"
                headerDescription=""
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        Audit Logs
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>

            <div className="mt-6 box">
                <div className="font-medium text-xl flex items-center">
                    <span className="mr-4">Audit Logs List</span>
                    <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                        <ContentLoading
                            loading={auditTrailLoading}
                            data={<> {auditTrail?.data?.totalDocs ?? '-'} audit {pluralize(auditTrail?.data?.totalDocs, "log", "logs")}</>}
                        />
                    </div>
                </div>
                <div className="flex flex-wrap justify-between mt-5">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-3">
                        <div>
                            <Filter filters={[
                                {
                                    title: "Date range",
                                    name: "dateRange",
                                    dataType: "custom-timeframe",
                                    hasResetButton: true,
                                    options: [
                                        {
                                            display: "Today",
                                            payload: "today",
                                        },
                                        {
                                            display: "Last 7 days",
                                            payload: "last_7_days",
                                        },
                                        {
                                            display: "30 days",
                                            payload: "30_days",
                                        },
                                        {
                                            display: "1 Year",
                                            payload: "1_year",
                                        },
                                    ],
                                },
                                {
                                    title: "Custom date range",
                                    dataType: "date-range",
                                    options: {
                                        from: {
                                            name: "startDate",
                                            placeholder: "Start Date",
                                        },
                                        to: {
                                            name: "endDate",
                                            placeholder: "End Date",
                                        },
                                    },
                                },
                                {
                                    title: "Module",
                                    name: "module",
                                    dataType: "select",
                                    options: [
                                        {
                                            display: "All", payload: ""
                                        },
                                        {
                                            display: "Payroll", payload: "payroll"
                                        },
                                        {
                                            display: "Payment", payload: "payment"
                                        },
                                        {
                                            display: "Workflow", payload: "workflow"
                                        },
                                        {
                                            display: "Team", payload: "team"
                                        },
                                        {
                                            display: "Role and Permission", payload: "Role and Permission"
                                        },
                                        {
                                            display: "Request Approval", payload: "request approval"
                                        }
                                    ]
                                },
                                {
                                    title: "Module Action",
                                    name: "module-action",
                                    dataType: "select",
                                    options: [
                                        {
                                            display: "All",
                                            payload: "",
                                        },
                                        {
                                            display: "Payroll Processing Initiated",
                                            payload: "Payroll Processing initiated",
                                        },
                                        {
                                            display: "Payroll Processing Approved",
                                            payload: "Payroll Processing approved",
                                        },
                                        {
                                            display: "Payroll Processing Rejected",
                                            payload: "Payroll Processing rejected",
                                        },
                                        {
                                            display: "Workflow Created",
                                            payload: "Workflow Created",
                                        },
                                        {
                                            display: "Request Created",
                                            payload: "Request Created",
                                        },
                                        {
                                            display: "Request Retrived",
                                            payload: "Request Retrived",
                                        },
                                        {
                                            display: "Request Comment",
                                            payload: "Request Comment",
                                        },
                                        {
                                            display: "Request Approved",
                                            payload: "Request Approved",
                                        },
                                        {
                                            display: "Request Rejected",
                                            payload: "Request Rejected",
                                        },
                                        {
                                            display: "Team Member Invited",
                                            payload: "Team Member invited",
                                        },
                                        {
                                            display: "Fetch Roles",
                                            payload: "Fetch Roles",
                                        },
                                        {
                                            display: "Role Created",
                                            payload: "Role Created",
                                        },
                                        {
                                            display: "Permission Retrived",
                                            payload: "Permission Retriveed",
                                        },
                                        {
                                            display: "Fetch Team Members",
                                            payload: "Fetch Team Members",
                                        },
                                        {
                                            display: "Team member Created",
                                            payload: "Team member created",
                                        },
                                        {
                                            display: "Modify Team Member",
                                            payload: "Modify Team Member",
                                        },
                                        {
                                            display: "Team Member Role Modify",
                                            payload: "Team member role modify",
                                        },
                                        {
                                            display: "Team Member Deactived",
                                            payload: "Team Member deactived",
                                        },
                                        {
                                            display: "Team Member Activated",
                                            payload: "Team Member Activated",
                                        },
                                        {
                                            display: "Team Member Resend Invite",
                                            payload: "Team Member Resend Invite",
                                        },
                                        {
                                            display: "Fetch Workflow Member",
                                            payload: "Fetch Workflow Member",
                                        },
                                        {
                                            display: "Modify Workflow",
                                            payload: "Modify Workflow",
                                        },
                                        {
                                            display: "Workflow Deleted",
                                            payload: "Workflow Deleted",
                                        },
                                    ],
                                },
                            ]}/>
                        </div>

                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download audit trail"
                                filename="Audit Trail.csv"
                                columns={[
                                    { label: "Date", key: "createdAt" },
                                    { label: "User Id", key: "user" },
                                    { label: "User Type", key: "type" },
                                    { label: "Name", key: "name" },
                                    { label: "Email Address", key: "email" },
                                    { label: "Business Id", key: "employer" },
                                    { label: "Action", key: "action" },
                                    { label: "Description", key: "description" },
                                    { label: "Request Method", key: "method" },
                                    { label: "URL Path", key: "path" },
                                    { label: "IP Address", key: "ip" },
                                ]}
                                click={() => {
                                    dispatch(auditTrailAction.downloadGetAuditTrail({...listParams, page: 1, limit: 2000})).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={auditTrailDownloadLoading}
                                response={auditTrailDownload?.data?.docs}
                            />
                        </div>

                        {/* <div>
                            <Print component={printComponentRef} />
                        </div> */}
                    </div>
                </div>

                <div>
                    <ActiveFilter filters={{"Start Date": "startDate", "End Date": "endDate"}} />
                </div>


                <div className="mt-6">

                        <div className="mb-4 text-xl hidden print:block">
                            Audit Trail
                        </div>

                        <Table>
                            <Table.Head>
                                <th>User</th>
                                <th>Module</th>
                                <th>Action</th>
                                <th>Date and Time</th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={auditTrailLoading}
                                    loadingText="Loading audit logs"
                                    data={auditTrail?.data?.data}
                                    colspan={4}
                                    emptyUI={<>
                                        <div className="py-20 mx-auto text-center text-base">
                                            <div className="flex justify-center">
                                                <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                            </div>
                                            <div className="text-[#101828] font-medium">
                                                No Data Available Yet
                                            </div>
                                            <div className="mt-1 text-[#4A5564]">
                                                Once you start activities, logs would appear here
                                            </div>
                                        </div>
                                    </>}
                                />

                                {auditTrailLoading === false && auditTrail?.data?.data?.map((log, index) =>
                                    <tr key={index}>
                                        <td>
                                            <div className="flex items-center gap-6">
                                                <div className="rounded-full w-12 h-12 flex justify-center items-center" style={{backgroundColor: getRandomColor()}}>
                                                    <div className="text-lg font-normal text-white">
                                                        {log?.businessMember?.firstName?.charAt().toUpperCase()}{log?.businessMember?.lastName?.charAt().toUpperCase()}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-[#101828] font-medium my-1">{log?.businessMember?.firstName || "N/A"} {log?.businessMember?.lastName || "N/A"}</div>
                                                    <div className="my-1">{log?.businessMember?.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="capitalize">
                                            {log?.module || "N/A"}
                                        </td>
                                        <td className="capitalize-first-letter">
                                            {log?.action || "N/A"}
                                        </td>
                                        <td>
                                            <div>
                                                {DateTimeFormat(log?.createdAt)}
                                            </div>
                                        </td>
                                        {/* <td>
                                            <Link to={{pathname:"mailto:" + log.email}} target="_blank" onClick={(e) => {window.open(e.target.getAttribute("href"), '_blank'); e.preventDefault(); }} className="hover:underline">{log.email}</Link>
                                        </td> */}
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={auditTrailLoading === false && auditTrail?.data?.data?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={auditTrail?.data} />
                            </div>
                        </Maybe>

                </div>
            </div>

        </div>
    )
}


export default List;